/**
 * This file implements the mechanism to configure the pageSettings object with
 * React. The following components allow us to read and write pageSettings that
 * can be used in AppShell routes.
 */
import * as React from "react";

import {PageDisplayContext} from "./page-display-container.tsx";
import type {PageSettings} from "./page-settings.ts";

type PageDisplayProviderProps = Readonly<{
    children: (props: Readonly<PageSettings>) => React.ReactNode;
}>;

/**
 * Provides access to the page settings.
 *
 * This component is used to consume the values sent from PageDisplayContainer
 * and pass those values to the children element.
 *
 * NOTE: This component was originally created to be used in the global Header
 * and Footer components. Be careful in case you need to use it.
 *
 * @example
 * <PageDisplayProvider>
 *  {({headerBackgroundColor, title, ...pageSettings}) =>
 *      <Header backgroundColor={headerBackgroundColor}>
 *          <h1>{title}</h1>
 *      </Header>
 *  }
 * </PageDisplayProvider>
 */
const PageDisplayProvider = (
    props: PageDisplayProviderProps,
): React.ReactElement => {
    return (
        <PageDisplayContext.Consumer>
            {({settings}) => props.children(settings)}
        </PageDisplayContext.Consumer>
    );
};

export default PageDisplayProvider;
